import React, { useCallback, useState } from 'react';
import { FaCommentAlt } from 'react-icons/fa';
import { HiOutlineEye } from 'react-icons/hi2';
import { MdOutlineAssignmentLate, MdOutlineModeEditOutline } from 'react-icons/md';
import { Link, useParams } from 'react-router-dom';
import { ActionIcon, Button, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../../../components/buttons/DeletePopover';
import StatusDropdown from '../../../../../components/forms/StatusDropdown';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import useContractStates from '../../../../../hooks/api-calls/contract/useContractStates';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { menuRoutes } from '../../../../../router/menu';
import { ContractService } from '../../../../../services/contract/contractService';
import { OrderService } from '../../../../../services/order/orderService';
import OrderProfileLayout from '../../OrderProfileLayout';
import ContractAssignmentModal from '../assignments/ContractAssignmentModal';
import ContractAssignmentFilters from './ContractAssignmentFilters';

const contractColumnOrder = [
    'id',
    'code',
    'currentContractStatus',
    'currentContractStatusDate',
    'productType',
    'product',
    'tutor',
    'client',
    'clientCompanyName',
    'clientCifNif',
    'studentId',
    'studentName',
    'studentFirstName',
    'studentLastName',
    'studentNif',
];

interface ContractAssignmentProps { }

const ContractAssignment: React.FC<ContractAssignmentProps> = ({ }) => {

    // HOOKS

    const { id } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getContractStatesList, isLoadingContractStates } = useContractStates({ active: true, action: 'edit', entity: 'contract' });
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters({ order: id }, [], 1, 50, { order: id });
    const service = new ContractService();
    const getContractProfilePath = (contractId: string) => {
        const basePath = `${menuRoutes.contracts.path}/${contractId}/profile${menuRoutes.contracts.profile.info}`;
        return id
            ? `${basePath}?fromOrderId=${id}&fromOrderView=assignments`
            : basePath;
    };

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [openAssignModal, setOpenAssignModal] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await (new OrderService).getOrderContracts(filters);
        return response.getResponseData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]));

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR ESTADO DEL CONTRATO
     * @EN CHANGE CONTRACT STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (id: string, stateId: string, comment: string, entityId: string, entityName: string) => {
        var response = { success: false };
        switch (entityName) {
            case 'contract':
                try {
                    response = await (await service.changeContractStatus(id, stateId, comment)).getResponseData();
                    if (response.success) {
                        toast.success('Estado del contrato actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado');
                }
                break;
            case 'product':
                try {
                    response = await (await service.changeContractProductStatus(id, stateId, entityId, comment)).getResponseData();
                    if (response.success) {
                        toast.success('Estado del producto actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado del producto');
                }
                break;
            case 'commission':
                try {
                    response = await (await service.changeContractCommissionStatus(id, stateId, comment)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de la comisión actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado de la comisión');
                }
                break;
            default:
                break;
        }
        refetch();
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESASIGNAR TUTOR DE CONTRATO
     * @EN UNASSIGN TUTOR FROM CONTRACT
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleUnassignTutorFromContract = async (id: string) => {
        // TODO: Implement multi unassign or remove this function from backend if not needed
        const ids = [id];
        const response = await (await (new OrderService).unassignTutor({ contractIds: ids })).getResponseData();
        if (response.success) {
            toast.success(response.message);
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR CONTRATO
     * @EN DELETE CONTRACT
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteContract(id)).getResponseData();
        if (response.success) {
            toast.success('Contrato eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR VARIOS CONTRATOS
     * @EN DELETE MULTIPLE CONTRACTS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiContracts(ids)).getResponseData();
        if (response.success) {
            toast.success('Contratos eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        contractColumnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    // RENDER

    return (
        <OrderProfileLayout>
            <>
                {!isLoadingContractStates && data !== undefined
                    ? (
                        <CustomTable
                            largeTable
                            id={'contracts-table'}
                            columnOrder={contractColumnOrder}
                            data={orderedData()}
                            isLoading={loading}
                            columnsNotShown={[
                                'currentContractStatusDate',
                                'clientCompanyName',
                                'clientCifNif',
                                'studentId',
                                'studentFirstName',
                                'studentLastName',
                                'studentNif',
                                'productType',
                            ]}
                            overrideColumns={[
                                {
                                    key: 'code',
                                    render: (row: any) => {
                                        return (
                                            userCan('get_contracts', 'contracts')
                                                ? (
                                                    <Link to={getContractProfilePath(row.row.original.id)}>
                                                        <Text className='font-bold text-primary'>{row.row.original.code}</Text>
                                                    </Link>
                                                )
                                                : <Text className='font-bold text-primary'>{row.row.original.code}</Text>
                                        );
                                    }
                                },
                                {
                                    key: 'currentContractStatus',
                                    render: (row: any) => {
                                        const canChangeStatus = userCan('edit_contracts', 'contracts') && userCan('get_statuses_for_select', 'statuses');
                                        return (
                                            <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                                <StatusDropdown
                                                    entityId={row.row.original?.id} 
                                                    disabled={!canChangeStatus}
                                                    title="¿Estás seguro de cambiar el estado del contrato?"
                                                    // Get the list of states for the current product type
                                                    statesOptions={getContractStatesList(null, id).filter((state: any) => row.row.original?.isDraft || !state.draftStatus)}
                                                    currentState={row.row.original?.currentContractStatus}
                                                    currentStateDate={row.row.original?.currentContractStatusDate?.date}
                                                    handleStateChange={(entityId: string, statusId: string, comment: string) =>
                                                        handleStateChange(entityId, statusId, comment, entityId, 'contract')
                                                    }
                                                />
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'product',
                                    render: (row: any) => {
                                        const product = row.row.original?.product;
                                        return (
                                            <div className='flex flex-col'>
                                                {product
                                                    ? (
                                                        <Link
                                                            to={`${menuRoutes.products.path}?filter_filters=%7B"active"%3Atrue%2C"search_text"%3A"${product.name}"%7D&filter_order=%5B%5D&page=1&limit=50`}
                                                            className='font-bold text-primary'
                                                        >
                                                            {product.code} - {product.name}
                                                        </Link>
                                                    )
                                                    : 'N/A'
                                                }
                                            </div>
                                        )
                                    }
                                },
                                {
                                    key: 'tutor',
                                    render: (row: any) => {
                                        const tutor = row.row.original.tutor;
                                        return (
                                            tutor
                                                ? userCan('get_tutors', 'tutors')
                                                    ? (
                                                        <>
                                                            <Link to={`${menuRoutes.tutors.path}/${tutor?.id}/profile${menuRoutes.tutors.profile.info}`}>
                                                                <Text className='font-bold text-primary'>{`${tutor?.name} ${tutor?.lastName || ''}`}</Text>
                                                            </Link>
                                                            {tutor?.nifCif && <div className='d-block'>{tutor?.nifCif}</div>}
                                                        </>
                                                    )
                                                    : <Text className='font-bold text-primary'>{`${tutor?.name} ${tutor?.lastName || ''}`}</Text>
                                                : <Text>N/A</Text>
                                        )
                                    },
                                },
                                {
                                    key: 'client',
                                    render: (row: any) => {
                                        const client = row.row.original?.client;
                                        return (
                                            <>
                                                {client
                                                    ? userCan('get_clients', 'clients')
                                                        ? (
                                                            <div>
                                                                <Link
                                                                    to={`${menuRoutes.clients.path}/${client?.id}/profile/info`}
                                                                    className='font-bold text-primary cursor-pointer d-block'
                                                                >
                                                                    {row.row.original?.clientCompanyName || 'N/A'}
                                                                </Link>
                                                                {row.row.original?.clientCifNif && <div className='d-block'>{row.row.original?.clientCifNif}</div>}
                                                                {userCan('get_clients_comments', 'clients') && client.lastComment && (
                                                                    <Tooltip
                                                                        size="sm"
                                                                        content={<div className='tooltip-container'>{client.lastComment}</div>}
                                                                        placement="top"
                                                                        color="invert"
                                                                    >
                                                                        <ActionIcon
                                                                            as="span"
                                                                            size="sm"
                                                                            variant="text"
                                                                            className="hover:!border-gray-900 hover:text-gray-700"
                                                                        >
                                                                            <FaCommentAlt className="h-4 w-4" size={20} />
                                                                        </ActionIcon>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        )
                                                        : 'N/A'
                                                    :
                                                    (<span className='font-bold text-primary'>{row.row.original?.clientCompanyName || row.row.original?.clientCifNif || 'N/A'}</span>)
                                                }
                                            </>
                                        )
                                    }
                                },
                                {
                                    key: 'studentName',
                                    render: (row: any) => {
                                        const name = row.row.original?.studentName
                                            ? `${row.row.original?.studentName} ${row.row.original?.studentFirstName ? ' ' + row.row.original?.studentFirstName : ''} ${row.row.original?.studentLastName ? ' ' + row.row.original?.studentLastName : ''}`
                                            : null;
                                        return (
                                            <>
                                                {row.row.original?.studentId
                                                    ? userCan('get_clients', 'clients')
                                                        ? (
                                                            <div>
                                                                <Link
                                                                    to={`${menuRoutes.students.path}/${row.row.original?.studentId}/profile/info`}
                                                                    className='font-bold text-primary cursor-pointer d-block'
                                                                >
                                                                    {name || 'N/A'}
                                                                </Link>
                                                                {row.row.original?.studentNif && <div className='d-block'>{row.row.original?.studentNif}</div>}
                                                            </div>
                                                        )
                                                        : 'N/A'
                                                    :
                                                    (<span>{name || row.row.original?.studentNif || 'N/A'}</span>)
                                                }
                                            </>
                                        )
                                    }
                                },
                            ]}
                            actions={[
                                {
                                    label: 'Desasignar Tutor',
                                    permissions: { group: 'contracts', permission: 'contract_assignment' },
                                    render: (row: any) => {
                                        if (!row.tutor) return null;
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Desasignar Tutor'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                    onClick={() => handleUnassignTutorFromContract(row.id)}
                                                >
                                                    <MdOutlineAssignmentLate className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Tooltip>
                                        );
                                    },
                                },
                                {
                                    label: 'View',
                                    permissions: { group: 'contracts', permission: 'get_contracts' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Ver detalles'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <Link to={getContractProfilePath(row.id)}>
                                                    <ActionIcon
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700"
                                                    >
                                                        <HiOutlineEye className="h-4 w-4" size={20} />
                                                    </ActionIcon>
                                                </Link>
                                            </Tooltip>
                                        );
                                    },
                                },
                                {
                                    label: 'Edit',
                                    permissions: { group: 'contracts', permission: 'edit_contracts' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Editar contrato'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <Link to={`${menuRoutes.contracts.path}/${row.id}/edit`}>
                                                    <ActionIcon
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700"
                                                    >
                                                        <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                                    </ActionIcon>
                                                </Link>
                                            </Tooltip>
                                        );
                                    },
                                },
                                {
                                    label: 'Delete',
                                    permissions: { group: 'contracts', permission: 'delete_contracts' },
                                    render: (row: any) => {
                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={'Eliminar'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <div>
                                                    <DeletePopover
                                                        title={`Eliminar contrato`}
                                                        description={`¿Estás seguro de que deseas eliminar el contrato ${row.code}?`}
                                                        onDelete={() => handleDelete(row.id)}
                                                    />
                                                </div>
                                            </Tooltip>
                                        );
                                    },
                                },
                            ]}
                            multiActions={[
                                {
                                    label: 'Asignar a tutor',
                                    permissions: { group: 'contracts', permission: 'contract_assignment' },
                                    render: (selectedRows: any) => {
                                        return (
                                            <Button
                                                variant={'outline'}
                                                color={'secondary'}
                                                size={'sm'}
                                                onClick={() => {
                                                    setOpenAssignModal(true);
                                                    setSelectedRows(selectedRows);
                                                    setSelectedProductIds(data?.data?.filter((contract: any) => selectedRows.includes(contract.id)).map((contract: any) => contract.product.id));

                                                }}
                                                className='p-3 text-nowrap'
                                            >
                                                Asignar Tutor
                                            </Button>
                                        )
                                    },
                                },
                            ]}
                            handleMultipleDelete={handleMultiDelete}
                            filters={filters}
                            updateFilters={updateFilters}
                            updateFilterOrder={updateFilterOrder}
                            defaultOrder={filters.filter_order || undefined}
                            paginationData={{
                                pageSize: filters.limit,
                                currentPage: filters.page,
                                pageCount: data ? data.lastPage : 1,
                                totalCount: data?.totalRegisters,
                                handlePagination: updatePage,
                                handlePerPage: updatePageSize,
                            }}
                            toggleFilters={() => setOpenFilters(!openFilters)}
                        />
                    )
                    : <Loader height="70vh" />
                }

                {openAssignModal && <ContractAssignmentModal ids={selectedRows} productIds={selectedProductIds} isOpen={openAssignModal} onClose={() => { setOpenAssignModal(false); refetch() }} />}

                <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Asignación de Contratos'}>
                    <ContractAssignmentFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
                </FilterDrawerView>
            </>
        </OrderProfileLayout>
    );
};

export default ContractAssignment;
